import s from './TagsRoll.module.sass'

import { FC, useContext, useState } from 'react'
import { KeenSliderInstance } from 'keen-slider/react'
import Link from '@shared/ui/Navigation/Link'

import { useAppSelector } from '@app/model/store/client'

import { TagsRollSkeleton } from '@pages/AppHome/ui/TagsRoll/index'
import { ContainerLong } from '@shared/ui/Layout/Containers/ContainerLong'

import { findDataTagForAnalyticBySlug } from '@features/Restaurant/RestaurantFilters/helpers'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { Carousel } from '@shared/ui/Layout/Carousel'
import { EGG } from '@shared/api/analytics'
import { Text } from '@shared/ui/Typography/Text'
import CarouselSlider from '@shared/ui/Layout/Carousel/CarouselSlider'
import { CarouselContext } from '@shared/ui/Layout/Carousel/CarouselContext'

import IconArrow from '@icons/icon-calendar-arrow.svg'

type TagItem = { id: number; name: string; slug: string }

type RenderTagItemParams = {
  tag: TagItem
  index: number
  total: number
  citySlug: string
  onOpenTag: (slug: string) => void
}

interface Props {
  tags: TagItem[]
  skeleton?: boolean
  className?: string
  citySlug?: string
}

type SliderState = {
  hasPrev: boolean
  hasNext: boolean
}

const getSliderState = (progress: number, maxIndex: number): SliderState => ({
  hasPrev: maxIndex > 0 && progress > 0.001,
  hasNext: maxIndex > 0 && progress < 0.999,
})

const TagsRollControls: FC<{ sliderState: SliderState }> = ({ sliderState }) => {
  const carouselState = useContext(CarouselContext)?.[0]

  const moveSliderByTwoTags = (direction: -1 | 1) => {
    const slider = carouselState?.instanceRef?.current as KeenSliderInstance | undefined
    if (!slider) return

    const { rel, maxIdx } = slider.track.details
    slider.moveToIdx(Math.min(maxIdx, Math.max(0, rel + direction * 2)))
  }

  return (
    <>
      <button
        className={`${s['tags-control']} ${s['tags-control--prev']} ${
          sliderState.hasPrev ? s['tags-control--visible'] : ''
        }`}
        type='button'
        disabled={!sliderState.hasPrev}
        onClick={() => moveSliderByTwoTags(-1)}>
        <IconArrow />
      </button>

      <button
        className={`${s['tags-control']} ${s['tags-control--next']} ${
          sliderState.hasNext ? s['tags-control--visible'] : ''
        }`}
        type='button'
        disabled={!sliderState.hasNext}
        onClick={() => moveSliderByTwoTags(1)}>
        <IconArrow />
      </button>
    </>
  )
}

/**
 * @function renderTagItem
 * рендерит элемент списка тегов с классом для последнего
 */
const renderTagItem = (params: RenderTagItemParams) => {
  const { tag, index, total, citySlug, onOpenTag } = params
  const itemClass = [s['tags-item'], index === total - 1 && s['tags-item--last']].filter(Boolean).join(' ')

  return (
    <Text sizes={'M S'} key={index} className={itemClass}>
      <Link
        href={`/${citySlug}/restaurants/restaurant_list/${tag.slug}/`}
        className={s['tags-link']}
        onClick={() => onOpenTag(tag.slug)}
        scroll={false}>
        {tag.name}
      </Link>
    </Text>
  )
}

const TagsRoll: FC<Props> = ({ tags, skeleton, className = '', citySlug: citySlugProp = '' }) => {
  const storeCurrentCity = useAppSelector((store) => store.global.currentCity)
  const citySlug = storeCurrentCity?.slug || citySlugProp
  const [sliderState, setSliderState] = useState<SliderState>({ hasPrev: false, hasNext: false })

  const updateSliderState = (progress: number, maxIndex: number) => {
    const nextState = getSliderState(progress, maxIndex)
    setSliderState((currentState) =>
      currentState.hasPrev === nextState.hasPrev && currentState.hasNext === nextState.hasNext
        ? currentState
        : nextState,
    )
  }

  const handleClickTag = (tagSlug: string) => {
    const dataTag = findDataTagForAnalyticBySlug({ foundSlug: tagSlug })
    void (dataTag?.type && EGG.common.tag_click({ filter_apply_type: dataTag.type, filter_apply_value: tagSlug }))
  }

  if (!skeleton && !tags?.length) return null
  if (!citySlug) return null

  return (
    <BlockBoundary>
      <ContainerLong>
        {skeleton ? (
          <TagsRollSkeleton />
        ) : (
          <section className={`${s['tags-roll']} ${className}`}>
            <h2 className='visually-hidden'>app home tags-roll</h2>
            <ul className={s['tags-list']}>
              <Carousel>
                <CarouselSlider
                  optionsKeenSlider={{
                    created(slider) {
                      updateSliderState(slider.track.details.progress, slider.track.details.maxIdx)
                    },
                    detailsChanged(slider) {
                      updateSliderState(slider.track.details.progress, slider.track.details.maxIdx)
                    },
                  }}>
                  {tags.map((tag, index) =>
                    renderTagItem({
                      tag,
                      index,
                      total: tags.length,
                      citySlug,
                      onOpenTag: handleClickTag,
                    }),
                  )}
                </CarouselSlider>
                <TagsRollControls sliderState={sliderState} />
              </Carousel>
            </ul>
          </section>
        )}
      </ContainerLong>
    </BlockBoundary>
  )
}

export default TagsRoll
